import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, Calendar, Info } from 'lucide-react';
import Layout from '../Layout';
import { CartManager } from '../../helpers/cart';
import { showToast } from '../../helpers/customToast';
import { saveAddress, checkLoggedIn, getCurrentUserData, updateUser, getCurrentUserAddress, createGuestAccount } from '../../helpers/user';
import { generatePassword, countriesList } from '../../helpers/tools';
import { createOrder } from '../../helpers/order';
import { createPaymentLink } from '../../helpers/stripe';
import DeliverySelection from '../DeliverySelection';
import { getData } from '../../helpers/ApiSettings';

const CheckoutPage = () => {
  const [cartItems, setCartItems] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [depositAmount, setDepositAmount] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState('');
  const [selectedZone, setSelectedZone] = useState('');
  const [selectedRelayPoint, setSelectedRelayPoint] = useState(null);
  const [deliveryPrice, setDeliveryPrice] = useState(0);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address_1: '',
    address_2: '',
    city: '',
    postal_code: '',
    comments: '',
    country: ''
  });

  useEffect(() => {
    const initializeCheckout = async () => {
      await checkAuthentication();
      fetchCartItems();
      // fetchCountries();
    };

    initializeCheckout();
  }, []);

  const validateCoupon = async (code) => {
    try {
      setIsValidatingCoupon(true);
      const response = await getData(`/coupons?filters[code][$eq]=${code}&populate=*`);

      if (!response.data.data?.[0]) {
        showToast('Code promo invalide', 'error');
        return;
      }

      const coupon = response.data.data[0];

      // Check if coupon is active
      if (!coupon.is_active) {
        showToast('Ce code promo n\'est plus valide', 'error');
        return;
      }

      // Check if coupon is applicable to rentals
      if (coupon.applicable_to_rentals === false) {
        showToast('Ce code promo n\'est pas valide pour les locations', 'error');
        return;
      }

      // Check validity period
      const now = new Date();
      const validFrom = new Date(coupon.valid_from);
      const validUntil = new Date(coupon.valid_until);

      if (now < validFrom || now > validUntil) {
        showToast('Ce code promo a expiré', 'error');
        return;
      }

      // Check if products in cart are eligible
      const couponProducts = coupon.products?.map(p => p.id) || [];
      if (couponProducts.length > 0) {
        const hasEligibleProduct = cartItems.some(item =>
          couponProducts.includes(item.product.id)
        );

        if (!hasEligibleProduct) {
          showToast('Ce code promo n\'est pas valide pour les articles de votre panier', 'error');
          return;
        }
      }

      // Check minimum amount
      if (coupon.min_amount && totalPrice < coupon.min_amount) {
        showToast(`Le montant minimum d'achat pour ce code est de ${coupon.min_amount}€`, 'error');
        return;
      }

      // Check if authorised for only one user
      if(coupon.user){
        if(isAuthenticated){
          // console.log(coupon.user);
          if(currentUserData.id !== coupon.user.id){
            showToast(`Vous ne pouvez pas utiliser ce coupon.`, 'error');
            return;
          }
        }else{
          showToast(`Merci de vous connecter afin de vérifier ce coupon.`, 'error');
          return;
        }
      }

      setAppliedCoupon(coupon);
      showToast('Code promo appliqué avec succès', 'success');
    } catch (error) {
      console.error('Error validating coupon:', error);
      showToast('Erreur lors de la validation du code promo', 'error');
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const calculateDiscount = () => {
    if (!appliedCoupon) return 0;

    let discount = 0;
    if (appliedCoupon.is_percentage) {
      discount = (totalPrice * appliedCoupon.amount) / 100;
    } else {
      discount = appliedCoupon.amount;
    }

    return Math.min(discount, (totalPrice+deliveryPrice)); // Don't exceed total price
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCountrySearch = async (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, country: value }));

    if (value.length > 1) {
      // setIsSearching(true);
      const results = await countriesList(value);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  const handleDeliveryMethodChange = (method) => {
    setSelectedDeliveryMethod(method);
    // Reset delivery price when changing method
    setDeliveryPrice(0);
  };

  const handleZoneChange = (zone) => {
    // Set delivery price
    if (zone) {
      setSelectedZone(zone.id);
      setDeliveryPrice(zone.price || 0);
      return;
    }
    setSelectedZone('');
  };

  const handleRelayPointChange = (point) => {
    setSelectedRelayPoint(point);
    // Set fixed price for relay points
    if (point) {
      setDeliveryPrice(point.price); // Example fixed price for relay points
    }
  };

  const selectCountry = (country) => {
    setFormData(prev => ({ ...prev, country: country }));
    setSearchResults([]);
  };

  const checkAuthentication = async () => {
    try {
      const authStatus = await checkLoggedIn();
      setIsAuthenticated(authStatus);

      if (authStatus) {
        const userData = await getCurrentUserData();
        // console.log(userData);
        let userAdress;
        if (userData.user_address?.id) {
          userAdress = await getCurrentUserAddress(userData.user_address.documentId);
          // console.log(userAdress);
        }
        setCurrentUserData(userData);

        setFormData(prevFormData => ({
          ...prevFormData,
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          email: userData.email || '',
          address_1: userAdress?.address_1 || '',
          address_2: userAdress?.address_2 || '',
          city: userAdress?.city || '',
          postal_code: userAdress?.postal_code || '',
          country: userAdress?.country || '',
          phone: userAdress?.phone || ''
        }));
      }
    } catch (error) {
      console.error('Authentication check failed:', error);
      showToast('Échec de la vérification du statut d\'authentification', 'error');
    }
  };

  const fetchCartItems = () => {
    try {
      const items = CartManager.getCart();
      const deposit = CartManager.getDeposit();
      const total = CartManager.getTotalPrice();

      setCartItems(items);
      setDepositAmount(deposit);
      setTotalPrice(total);
    } catch (error) {
      console.error('Error fetching cart items:', error);
      showToast('Échec du chargement des articles du panier', 'error');
    }
  };

  const handleGuestCheckout = async () => {
    try {
      const password = generatePassword();
      const userData = {
        username: formData.email,
        password,
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name
      };

      const newUser = await createGuestAccount(userData);
      // console.log(newUser)
      if (!newUser.success) {
        throw new Error('Failed to create guest account');
      }
      setCurrentUserData(newUser.user);

      return newUser.user.id;
    } catch (error) {
      console.error('Guest checkout failed:', error);
      throw new Error('Failed to create account for checkout');
    }
  };

  const handleAddress = async (userId) => {
    const addressData = {
      address_1: formData.address_1,
      address_2: formData.address_2,
      country: formData.country,
      postal_code: formData.postal_code,
      city: formData.city,
      phone: formData.phone,
      user: userId
    };

    try {
      await saveAddress({ data: addressData });
    } catch (error) {
      console.error('Failed to save address:', error);
      throw new Error('Failed to save shipping address');
    }
  };

  const validateForm = () => {
    const requiredFields = {
      first_name: 'Prénom',
      last_name: 'Nom',
      email: 'E-mail',
      phone: 'Téléphone',
      address_1: 'Adresse',
      country: 'Pays',
      city: 'Ville',
      postal_code: 'Code Postal'
    };

    for (const [field, label] of Object.entries(requiredFields)) {
      if (!formData[field]?.trim()) {
        throw new Error(`Le champ "${label}" est requis.`);
      }
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      throw new Error('Veuillez saisir une adresse e-mail valide');
    }

    if (!selectedDeliveryMethod) {
      throw new Error('Veuillez sélectionner un mode de livraison');
    }

    if (selectedDeliveryMethod === 'home_delivery' && !selectedZone) {
      throw new Error('Veuillez sélectionner une zone de livraison');
    }

    if (selectedDeliveryMethod === 'relay_point' && !selectedRelayPoint) {
      throw new Error('Veuillez sélectionner un point relais');
    }
  };

  const processOrder = async (orderData) => {
    try {
      // console.log('Starting order process with data:', orderData);

      // Validate cart items availability
      const validationResult = await CartManager.validateAndRemoveUnavailableItems();

      if (validationResult.hasRemovedItems) {
        throw new Error('Some items in your cart are no longer available. Please review your cart.');
      }

      // Create the order using helper
      // console.log('Creating order...', orderData);
      const orderResult = await createOrder({
        ...orderData,
        status: 'pending_payment'
      });

      // console.log('Order creation result:', orderResult);

      if (!orderResult.success) {
        throw new Error(orderResult.error || 'Failed to create order');
      }

      // Create Stripe payment link
      const paymentLinkResult = await createPaymentLink({
        orderId: orderResult.orderId,
        documentId: orderResult.documentId,
        totalAmount: orderData.totalAmount,
        customerEmail: formData.email,
        firstName: formData.first_name,
        lastName: formData.last_name
      });

      // console.log('Payment link result:', paymentLinkResult);

      // If successful, clear the cart
      // await CartManager.clearCart();

      return {
        orderId: orderResult.orderId,
        paymentUrl: paymentLinkResult.url
      };
    } catch (error) {
      console.error('Error in processOrder:', error);
      throw new Error(error.message || 'Failed to process order');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      validateForm();
      let userId;

      if (!isAuthenticated) {
        userId = await handleGuestCheckout();
      } else {
        userId = currentUserData.id;
        await updateUser(userId, {
          first_name: formData.first_name,
          last_name: formData.last_name
        });
      }
      await handleAddress(userId);

      const orderData = {
        userId,
        items: cartItems,
        shippingAddress: {
          address_1: formData.address_1,
          address_2: formData.address_2,
          country: formData.country,
          postal_code: formData.postal_code,
          city: formData.city,
          phone: formData.phone,
        },
        delivery: {
          method: selectedDeliveryMethod,
          // zone: selectedZone,
          price: deliveryPrice
        },
        totalAmount: (totalPrice + deliveryPrice) - calculateDiscount(),
        depositAmount,
        comments: formData.comments,
        coupon: appliedCoupon ? {
          id: appliedCoupon.id,
          code: appliedCoupon.code,
          discount: calculateDiscount()
        } : null,
        userDetails: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          address_1: formData.address_1,
          address_2: formData.address_2,
          country: formData.country,
          postal_code: formData.postal_code,
          city: formData.city,
          phone: formData.phone,
        },
        relayPoint: selectedDeliveryMethod === 'relay_point' ? selectedRelayPoint.id : null,
      };

      const result = await processOrder(orderData);

      // Redirect to Stripe payment page
      window.location.href = result.paymentUrl;
    } catch (error) {
      console.error('Checkout failed:', error);
      showToast(error.message || 'Une erreur est survenue, merci de réessayer.', 'error');
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <Layout>
      <div className="container my-5">
        <h1 className="mb-4">Paiement</h1>
        {!isAuthenticated && (
          <div className="card mb-4">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-md-8">
                  <h5 className="mb-0">Déjà client ?</h5>
                  <p className="text-muted mb-0">
                    Connectez-vous pour un paiement plus rapide et suivre vos commandes
                  </p>
                </div>
                <div className="col-md-4 text-end">
                  <Link
                    to="/connexion?redirect=checkout"
                    className="btn btn-outline-primary"
                  >
                    Se connecter
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="row">
          <div className="col-md-8">
            <div className="card mb-4">
              <div className="card-body">
                <h3 className="card-title">Dépôt de Garantie</h3>
                <div className="alert alert-info" role="alert">
                  <Info size={18} className="me-2" />
                  Un dépôt de garantie de {depositAmount.toFixed(2)}€ sera requis pour votre réservation.
                  Vous recevrez un email de Swikly pour effectuer ce dépôt de manière sécurisée.
                  Le dépôt ne sera pas débité de votre compte, mais simplement pré-autorisé.
                </div>
              </div>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="card mb-4 overflow-visible">
                <div className="card-body">
                  <h3 className="card-title">Informations Personnelles</h3>
                  {isAuthenticated && (
                    <p className='mb-3 text-secondary'><i>Votre compte sera mis à jour avec ces informations.</i></p>
                  )}
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="last_name" className="form-label">Nom <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        id="last_name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="first_name" className="form-label">Prénom <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        id="first_name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="email" className="form-label">E-mail <span className="text-danger">*</span></label>
                      <input
                        type="email"
                        className="form-control"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        readOnly={isAuthenticated}
                        disabled={isAuthenticated}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="phone" className="form-label">Téléphone <span className="text-danger">*</span></label>
                      <input
                        type="tel"
                        className="form-control"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label htmlFor="address" className="form-label">Adresse <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control mb-2"
                        id="address_1"
                        name="address_1"
                        value={formData.address_1}
                        onChange={handleInputChange}
                        required
                      />
                      <input
                        type="text"
                        className="form-control"
                        id="address_2"
                        name="address_2"
                        value={formData.address_2}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-12">
                      <label htmlFor="country" className="form-label">Pays <span className="text-danger">*</span></label>
                      <div className="position-relative">
                        <input
                          type="text"
                          className="form-control"
                          id="country"
                          name="country"
                          value={formData.country}
                          onChange={handleCountrySearch}
                          autoComplete="off"
                          required
                        />
                        {searchResults.length > 0 && (
                          <div className="position-absolute w-100 mt-1 bg-light border rounded shadow-sm" style={{ zIndex: 1000, height: 100, overflow: 'scroll' }}>
                            {searchResults.map((country, index) => (
                              <div
                                key={index}
                                className="p-2 cursor-pointer hover:bg-white"
                                style={{ cursor: 'pointer' }}
                                onClick={() => selectCountry(country.name_fr)}
                              >
                                {country.name_fr}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="city" className="form-label">Ville <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="postal_code" className="form-label">Code Postal <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        id="postal_code"
                        name="postal_code"
                        value={formData.postal_code}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="card mb-4">
                <div className="card-body">
                  <h3 className="card-title">Informations Supplémentaires <small className='text-secondary'><i>(optionel)</i></small></h3>
                  <p>Si vous avez des demandes particulières merci de les inqiquer ici.</p>
                  <div className="col-md-12">
                    <textarea className="form-control" id="comments" name="comments" required onChange={handleInputChange}></textarea>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div className="col-md-4">
            <div className="card mb-4">
              <div className="card-body">
                <h3 className="card-title">Récapitulatif</h3>
                {cartItems.map((item, index) => (
                  <div key={index} className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h6 className="my-0">{item.product.titre}</h6>
                      <small className="text-muted">
                        <Calendar size={14} className="me-1" />
                        {formatDate(item.startDate)} - {formatDate(item.endDate)}
                      </small>
                      <br />
                      <small className="text-muted">
                        Quantité : {item.quantity || 1}
                      </small>
                      <br />
                    </div>
                    <span className="text-muted">{(item.price * (item.quantity || 1)).toFixed(2)}€</span>
                  </div>
                ))}

                <hr className="my-4" />
                <div className="mb-3">
                  <label htmlFor="coupon" className="form-label">Code promo</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="coupon"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Entrez votre code"
                      disabled={isValidatingCoupon || appliedCoupon}
                    />
                    {!appliedCoupon ? (
                      <button
                        className="btn btn-outline-primary"
                        type="button"
                        onClick={() => validateCoupon(couponCode)}
                        disabled={!couponCode || isValidatingCoupon}
                      >
                        {isValidatingCoupon ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        ) : 'Appliquer'}
                      </button>
                    ) : (
                      <button
                        className="btn btn-outline-danger"
                        type="button"
                        onClick={() => {
                          setAppliedCoupon(null);
                          setCouponCode('');
                        }}
                      >
                        Retirer
                      </button>
                    )}
                  </div>
                  {appliedCoupon && (
                    <small className="text-success">
                      Code promo appliqué : {appliedCoupon.is_percentage ? `${appliedCoupon.amount}%` : `${appliedCoupon.amount}€`} de réduction
                    </small>
                  )}
                </div>

                <hr className="my-4" />
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span>Sous-total</span>
                  <span>{totalPrice.toFixed(2)}€</span>
                </div>
                {deliveryPrice > 0 && (
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span>Frais de livraison</span>
                    <span>{deliveryPrice.toFixed(2)}€</span>
                  </div>
                )}
                {appliedCoupon && (
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span>Réduction</span>
                    <span className="text-success">-{calculateDiscount().toFixed(2)}€</span>
                  </div>
                )}
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="m-0">Total</h5>
                  <h5 className="m-0">{((totalPrice + deliveryPrice) - calculateDiscount()).toFixed(2)}€</h5>
                </div>
              </div>
            </div>
            <DeliverySelection
              selectedMethod={selectedDeliveryMethod}
              selectedZone={selectedZone}
              selectedRelayPoint={selectedRelayPoint}
              deliveryPrice={deliveryPrice}
              onMethodChange={handleDeliveryMethodChange}
              onZoneChange={handleZoneChange}
              onRelayPointChange={handleRelayPointChange}
            />
            <div className="card mb-4">
              <div className="card-body">
                <h3 className="card-title">Mode de paiement</h3>
                <input type="radio" defaultChecked /> Carte bancaire (Stripe) <CreditCard size={18} />
              </div>
            </div>

            <div className="form-check mb-5">
              <input
                type="checkbox"
                name="acceptance"
                id="acceptance"
                rows={4}
                required
                // onChange={handleChange}
                className="form-check-input"
                // checked={formData.acceptance}
              />
              <label htmlFor="acceptance" className="form-check-label">
                J'accepte les <a href="/page/cgv" target="_blank">Conditions Générales</a> <span className="text-danger">*</span>
              </label>
            </div>

            <button type="submit" onClick={e => handleSubmit(e)} className="btn btn-primary btn-lg w-100">Commander & Payer</button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CheckoutPage;